import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from './Navigation';
import { api } from '../services/api';
import './Transactions.css';

interface Account {
  accountId: number;
  accountName: string;
  accountType: string;
  bankName?: string;
  currency: string;
  currentBalance: number;
  isDefault: boolean;
}

interface Transaction {
  transactionId: number;
  transactionName: string;
  amount: number;
  type: 'expense' | 'income';
  category: string;
  createdAt: string;
  transactionDate: string;
  userId: number;
  account?: Account;
  accountId?: number;
}

interface AccountTotals {
  accountId: number;
  accountName: string;
  accountType: string;
  currentBalance: number;
  currency: string;
  totalIncome: number;
  totalExpenses: number;
  totalAmount: number;
  transactionCount: number;
  transactions: Transaction[];
}

interface TotalsResponse {
  userId: number;
  accounts: AccountTotals[];
  grandTotalIncome: number;
  grandTotalExpenses: number;
  grandTotalAmount: number;
  totalTransactionCount: number;
}

const TRANSACTION_TYPES = ['expense', 'income'] as const;
const CATEGORIES = [
  'Food',
  'Transportation',
  'Rent',
  'Saloon',
  'Shopping',
  'Bills',
  'Entertainment',
  'Kids Education',
  'Healthcare',
  'Education',
  'Salary',
  'Housemaid',
  'Investment',
  'Remittance',
  'Other'
];


const Transactions: React.FC = () => {
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [totals, setTotals] = useState<TotalsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [displayCount, setDisplayCount] = useState(10); // Number of transactions to display initially
  
  // Form state
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [formData, setFormData] = useState({
    transaction_name: '',
    amount: '',
    type: 'expense' as 'expense' | 'income',
    category: 'Food',
    transactionDate: new Date().toISOString().split('T')[0],
    accountId: ''
  });

  // Filter state
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });
  const [selectedMonth, setSelectedMonth] = useState<string>('');

  const fetchAccounts = useCallback(async () => {
    if (!userId) return;
    
    try {
      const response = await api.get(`/accounts/user/${userId}/active`);
      if (!response.ok) throw new Error('Failed to fetch accounts');
      const data = await response.json();
      setAccounts(data);
      // Set default account if available
      const defaultAccount = data.find((acc: Account) => acc.isDefault);
      if (defaultAccount && !formData.accountId) {
        setFormData(prev => ({ ...prev, accountId: defaultAccount.accountId.toString() }));
      }
    } catch (err) {
      console.error('Error fetching accounts:', err);
    }
  }, [userId]);

  const fetchTransactions = useCallback(async () => {
    if (!userId) return;
    
    try {
      const response = await api.get(`/transactions?userId=${userId}`);
      if (!response.ok) throw new Error('Failed to fetch transactions');
      const data = await response.json();
      setTransactions(data);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Failed to load transactions');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  const fetchTotals = useCallback(async () => {
    if (!userId) return;
    
    try {
      const response = await api.get(`/transactions/user/${userId}/total-expenses`);
      if (!response.ok) throw new Error('Failed to fetch totals');
      const data: TotalsResponse = await response.json();
      setTotals(data);
    } catch (err) {
      console.error('Error fetching totals:', err);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      navigate('/');
      return;
    }
    fetchAccounts();
    fetchTransactions();
    fetchTotals();
  }, [userId, navigate, fetchAccounts, fetchTransactions, fetchTotals]);

  // Reset display count when filters change
  useEffect(() => {
    setDisplayCount(10);
  }, [dateFilter.startDate, dateFilter.endDate, selectedMonth]);

  // Filter transactions based on date
  const getFilteredTransactions = () => {
    let filtered = transactions;

    if (dateFilter.startDate) {
      filtered = filtered.filter(t => {
        const transactionDate = t.transactionDate || t.createdAt.split('T')[0];
        return transactionDate >= dateFilter.startDate;
      });
    }

    if (dateFilter.endDate) {
      filtered = filtered.filter(t => {
        const transactionDate = t.transactionDate || t.createdAt.split('T')[0];
        return transactionDate <= dateFilter.endDate;
      });
    }

    if (selectedMonth) {
      filtered = filtered.filter(t => {
        const transactionDate = t.transactionDate || t.createdAt.split('T')[0];
        return transactionDate.startsWith(selectedMonth);
      });
    }

    return filtered;
  };



  const handleAddTransaction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;

    if (!formData.transaction_name.trim() || !formData.amount || !formData.transactionDate || !formData.accountId) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const response = await api.post(`/transactions`, {
        transaction_name: formData.transaction_name.trim(),
        amount: parseFloat(formData.amount),
        type: formData.type,
        category: formData.category,
        transaction_date: formData.transactionDate,
        account: {
          accountId: parseInt(formData.accountId)
        },
        user: {
          userId: parseInt(userId)
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ errorMessage: 'Failed to add transaction' }));
        throw new Error(errorData.errorMessage || 'Failed to add transaction');
      }

      // Refresh data
      await fetchTransactions();
      await fetchAccounts(); // Refresh accounts to get updated balances
      await fetchTotals();
      closeModal();
    } catch (err: any) {
      console.error('Error adding transaction:', err);
      setError(err.message || 'Failed to add transaction');
    }
  };

  const handleUpdateTransaction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId || !editingTransaction) return;

    if (!formData.transaction_name.trim() || !formData.amount || !formData.transactionDate || !formData.accountId) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const response = await api.put(`/transactions/${editingTransaction.transactionId}`, {
        transaction_name: formData.transaction_name.trim(),
        amount: parseFloat(formData.amount),
        type: formData.type,
        category: formData.category,
        transaction_date: formData.transactionDate,
        account: {
          accountId: parseInt(formData.accountId)
        },
        user: {
          userId: parseInt(userId)
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ errorMessage: 'Failed to update transaction' }));
        throw new Error(errorData.errorMessage || 'Failed to update transaction');
      }

      // Refresh data
      await fetchTransactions();
      await fetchAccounts(); // Refresh accounts to get updated balances
      await fetchTotals();
      closeModal();
    } catch (err: any) {
      console.error('Error updating transaction:', err);
      setError(err.message || 'Failed to update transaction');
    }
  };

  const handleDeleteTransaction = async (transactionId: number) => {
    if (!userId) return;
    
    if (!window.confirm('Are you sure you want to delete this transaction?')) {
      return;
    }

    try {
      const response = await api.delete(`/transactions/${transactionId}`);

      if (!response.ok) throw new Error('Failed to delete transaction');

      // Refresh data
      await fetchTransactions();
      await fetchAccounts(); // Refresh accounts to get updated balances
      await fetchTotals();
    } catch (err) {
      console.error('Error deleting transaction:', err);
      setError('Failed to delete transaction');
    }
  };

  const openEditModal = (transaction: Transaction) => {
    setEditingTransaction(transaction);
    const transactionDate = transaction.transactionDate || transaction.createdAt.split('T')[0];
    const defaultAccount = accounts.find(acc => acc.isDefault);
    setFormData({
      transaction_name: transaction.transactionName,
      amount: transaction.amount.toString(),
      type: transaction.type,
      category: transaction.category,
      transactionDate: transactionDate,
      accountId: transaction.accountId?.toString() || transaction.account?.accountId?.toString() || defaultAccount?.accountId.toString() || '',
    });
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingTransaction(null);
    const defaultAccount = accounts.find(acc => acc.isDefault);
    setFormData({
      transaction_name: '',
      amount: '',
      type: 'expense',
      category: 'Food',
      transactionDate: new Date().toISOString().split('T')[0],
      accountId: defaultAccount?.accountId.toString() || '',
    });
    setError('');
  };


  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateOnly = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const clearFilters = () => {
    setDateFilter({ startDate: '', endDate: '' });
    setSelectedMonth('');
    setDisplayCount(10); // Reset display count when filters change
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KW', {
      style: 'decimal',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(amount) + ' KD';
  };

  if (isLoading) {
    return (
      <div className="transactions-container">
        <Navigation />
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="transactions-container">
      <Navigation />

      {/* Grand Totals Section */}
      {totals && (
        <div className="totals-section">
          <div className="grand-totals-header">
            <div className="section-header">
              <span className="section-icon">📊</span>
              <h2>Grand Totals (All Accounts)</h2>
            </div>
          </div>
          <div className="grand-totals-cards">
            <div className="total-card income">
              <div className="total-card-header">
                <div className="total-card-icon-wrapper income">
                  <span className="total-card-icon">💰</span>
                </div>
                <h3>Total Income</h3>
              </div>
              <p className="total-amount income">{formatCurrency(totals.grandTotalIncome)}</p>
            </div>
            <div className="total-card expense">
              <div className="total-card-header">
                <div className="total-card-icon-wrapper expense">
                  <span className="total-card-icon">💸</span>
                </div>
                <h3>Total Expenses</h3>
              </div>
              <p className="total-amount expense">{formatCurrency(totals.grandTotalExpenses)}</p>
            </div>
            <div className="total-card balance">
              <div className="total-card-header">
                <div className="total-card-icon-wrapper balance">
                  <span className="total-card-icon">💵</span>
                </div>
                <h3>Total Balance</h3>
              </div>
              <p className={`total-amount balance ${totals.grandTotalAmount >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(totals.grandTotalAmount)}
              </p>
            </div>
          </div>

          {/* Per-Account Totals Section */}
          {totals.accounts && totals.accounts.length > 0 && (
            <div className="account-totals-section">
              <div className="section-header">
                <span className="section-icon">🏦</span>
                <h2 className="account-totals-header">Per-Account Breakdown</h2>
              </div>
              <div className="account-totals-grid">
                {totals.accounts.map((account) => (
                  <div key={account.accountId} className="account-totals-card">
                    <div className="account-totals-card-header">
                      <h3>{account.accountName}</h3>
                      <span className="account-type-badge">{account.accountType}</span>
                    </div>
                    <div className="account-totals-details">
                      <div className="account-total-item">
                        <span className="account-total-label">Current Balance:</span>
                        <span className={`account-total-value balance ${account.currentBalance >= 0 ? 'positive' : 'negative'}`}>
                          {formatCurrency(account.currentBalance)}
                        </span>
                      </div>
                      <div className="account-total-item">
                        <span className="account-total-label">Income:</span>
                        <span className="account-total-value income">{formatCurrency(account.totalIncome)}</span>
                      </div>
                      <div className="account-total-item">
                        <span className="account-total-label">Expenses:</span>
                        <span className="account-total-value expense">{formatCurrency(account.totalExpenses)}</span>
                      </div>
                      <div className="account-total-item">
                        <span className="account-total-label">Net Amount:</span>
                        <span className={`account-total-value ${account.totalAmount >= 0 ? 'positive' : 'negative'}`}>
                          {formatCurrency(account.totalAmount)}
                        </span>
                      </div>
                      <div className="account-total-item">
                        <span className="account-total-label">Transactions:</span>
                        <span className="account-total-value">{account.transactionCount}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="transactions-content">
        <div className="transactions-header-section">
          <div className="section-header">
            <span className="section-icon">💳</span>
            <h2>Transactions</h2>
          </div>
          <button className="add-button" onClick={() => setShowAddModal(true)}>
            <span className="button-icon">+</span>
            Add Transaction
          </button>
        </div>

        {/* Date Filter Section */}
        <div className="filter-section">
          <div className="filter-header">
            <span className="filter-icon">🔍</span>
            <h3 className="filter-title">Filter Transactions</h3>
          </div>
          <div className="filter-controls">
            <div className="filter-group">
              <label htmlFor="startDate" className="filter-label">Start Date</label>
              <input
                type="date"
                id="startDate"
                className="filter-input"
                value={dateFilter.startDate}
                onChange={(e) => setDateFilter({ ...dateFilter, startDate: e.target.value })}
              />
            </div>
            <div className="filter-group">
              <label htmlFor="endDate" className="filter-label">End Date</label>
              <input
                type="date"
                id="endDate"
                className="filter-input"
                value={dateFilter.endDate}
                onChange={(e) => setDateFilter({ ...dateFilter, endDate: e.target.value })}
              />
            </div>
            <div className="filter-group">
              <label htmlFor="month" className="filter-label">Month</label>
              <input
                type="month"
                id="month"
                className="filter-input"
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  setDateFilter({ startDate: '', endDate: '' });
                }}
              />
            </div>
            <button className="clear-filter-button" onClick={clearFilters}>
              Clear Filters
            </button>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="transactions-list">
          {getFilteredTransactions().length === 0 ? (
            <div className="empty-state">
              <p>{transactions.length === 0 ? 'No transactions yet. Add your first transaction!' : 'No transactions match the selected filters.'}</p>
            </div>
          ) : (
            <>
              {getFilteredTransactions().slice(0, displayCount).map((transaction) => (
                <div key={transaction.transactionId} className="transaction-item">
                  <div className="transaction-icon-wrapper">
                    <div className={`transaction-icon ${transaction.type}`}>
                      {transaction.type === 'income' ? '📈' : '📉'}
                    </div>
                  </div>
                  <div className="transaction-info">
                    <div className="transaction-main">
                      <h3 className="transaction-name">{transaction.transactionName}</h3>
                      <span className={`transaction-type-badge ${transaction.type}`}>
                        {transaction.type}
                      </span>
                    </div>
                    <div className="transaction-details">
                      <span className="transaction-category">
                        <span className="category-icon">🏷️</span>
                        {transaction.category}
                      </span>
                      {transaction.account && (
                        <span className="transaction-account">
                          <span className="account-icon">🏦</span>
                          {transaction.account.accountName}
                        </span>
                      )}
                      <span className="transaction-date">
                        <span className="date-icon">📅</span>
                        {transaction.transactionDate 
                          ? formatDateOnly(transaction.transactionDate) 
                          : formatDate(transaction.createdAt)}
                      </span>
                    </div>
                  </div>
                  <div className="transaction-amount-section">
                    <p className={`transaction-amount ${transaction.type}`}>
                      {transaction.type === 'income' ? '+' : '-'}
                      {formatCurrency(Math.abs(transaction.amount))}
                    </p>
                    <div className="transaction-actions">
                      <button
                        className="action-button edit-button"
                        onClick={() => openEditModal(transaction)}
                        aria-label="Edit transaction"
                        title="Edit"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      </button>
                      <button
                        className="action-button delete-button"
                        onClick={() => handleDeleteTransaction(transaction.transactionId)}
                        aria-label="Delete transaction"
                        title="Delete"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {getFilteredTransactions().length > displayCount && (
                <div className="load-more-container">
                  <button 
                    className="load-more-button"
                    onClick={() => setDisplayCount(prev => prev + 10)}
                  >
                    Load More ({getFilteredTransactions().length - displayCount} remaining)
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingTransaction ? 'Edit Transaction' : 'Add New Transaction'}
              </h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <form
              onSubmit={editingTransaction ? handleUpdateTransaction : handleAddTransaction}
              className="transaction-form"
            >
              <div className="form-group">
                <label htmlFor="transaction_name" className="form-label">
                  Transaction Name
                </label>
                <input
                  type="text"
                  id="transaction_name"
                  className="form-input"
                  placeholder="e.g., Rent, Salary, Groceries"
                  value={formData.transaction_name}
                  onChange={(e) => {
                    setFormData({ ...formData, transaction_name: e.target.value });
                    setError('');
                  }}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="amount" className="form-label">
                  Amount
                </label>
                <input
                  type="number"
                  id="amount"
                  className="form-input"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => {
                    setFormData({ ...formData, amount: e.target.value });
                    setError('');
                  }}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="type" className="form-label">
                  Type
                </label>
                <select
                  id="type"
                  className="form-input"
                  value={formData.type}
                  onChange={(e) => {
                    setFormData({ ...formData, type: e.target.value as 'expense' | 'income' });
                    setError('');
                  }}
                  required
                >
                  {TRANSACTION_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="category" className="form-label">
                  Category
                </label>
                <select
                  id="category"
                  className="form-input"
                  value={formData.category}
                  onChange={(e) => {
                    setFormData({ ...formData, category: e.target.value });
                    setError('');
                  }}
                  required
                >
                  {CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="accountId" className="form-label">
                  Account *
                </label>
                <select
                  id="accountId"
                  className="form-input"
                  value={formData.accountId}
                  onChange={(e) => {
                    setFormData({ ...formData, accountId: e.target.value });
                    setError('');
                  }}
                  required
                >
                  <option value="">Select an account</option>
                  {accounts.map((account) => (
                    <option key={account.accountId} value={account.accountId}>
                      {account.accountName} {account.isDefault ? '(Default)' : ''} - {account.currency}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="transactionDate" className="form-label">
                  Transaction Date *
                </label>
                <input
                  type="date"
                  id="transactionDate"
                  className="form-input"
                  value={formData.transactionDate}
                  onChange={(e) => {
                    setFormData({ ...formData, transactionDate: e.target.value });
                    setError('');
                  }}
                  required
                />
              </div>


              <button type="submit" className="submit-button">
                {editingTransaction ? 'Update Transaction' : 'Add Transaction'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transactions;

